import { Story } from '@src/api/types'
import { isServer } from '@src/lib/utils'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'
import { getAnonymousID } from '@src/lib/whoami'

export interface StoryKeyValues {
  [key: string]: any
}

export function useStoryKeyValues(story: Story, params?: { [key: string]: any }): StoryKeyValues {
  const searchParams = useSearchParams()
  const adtar = searchParams.get('adtar')

  const values = useMemo(() => {
    const threats = story?.verity?.threats?.map((threat) => ({
      [`verity_${threat.id.toLowerCase()}_conf`]: threat.confidence,
      [`verity_${threat.id.toLowerCase()}_risk`]: threat.confidence
    }))

    const keyValues = {
      authorId: story?.author?.id,
      category: story?.category?.length ? story.category[0].name : 'No category',
      tags: story?.tags?.length ? story.tags : null,
      brandName: story?.brand_name,
      brandId: story?.brand_id,
      pagetype: story?.type,
      ...(story?.nsfw && { tags: [...(story?.tags || []), 'NSFW'] }),
      verity_keywords: story?.verity?.keywords,
      verity_iabv1: story?.verity?.iab?.v1?.map((v1) => v1.id) ?? [],
      verity_iabv2: story?.verity?.iab?.v2?.map((v2) => v2.id) ?? [],
      verity_iabv3: story?.verity?.iab?.v3?.map((v3) => v3.id) ?? [],
      verity_neutral: story?.verity?.sentiments?.find(({ sentiment }) => sentiment === 'neutral')
        ?.score,
      verity_positive: story?.verity?.sentiments?.find(({ sentiment }) => sentiment === 'positive')
        ?.score,
      verity_negative: story?.verity?.sentiments?.find(({ sentiment }) => sentiment === 'negative')
        ?.score,
      adtar,
      ...params
    }

    return (threats ?? []).reduce((acc, obj) => ({ ...acc, ...obj }), keyValues)
  }, [story, adtar, params])

  return values
}

export function getHTLVideoParams(): Promise<{ [key: string]: string }> {
  return new Promise((resolve, reject) => {
    window.pbjs.que.push(async () => {
      const videoAdUnit: any = {
        code: 'video1',
        mediaTypes: {
          video: {
            context: 'instream',
            playerSize: [[400, 300]],
            mimes: ['video/mp4', 'application/javascript'],
            protocols: [1, 2, 3, 4, 5, 6, 7, 8],
            playbackmethod: [3],
            maxduration: 15,
            skip: 1,
            pos: 1,
            placement: 1,
            plcmt: 1,
            api: [2],
            linearity: 1
          }
        }
      }

      try {
        videoAdUnit.bids = window.htlbid.config.prebidGroups['video-instream']
        window.pbjs.addAdUnits(videoAdUnit)

        window.pbjs.setConfig({
          debug: true,
          cache: {
            url: 'https://prebid.adnxs.com/pbc/v1/cache'
          }
        })

        const params = await window.pbjs.requestBids()

        if (params?.bids?.video1?.bids[0]) {
          const bids = params?.bids?.video1?.bids[0]

          const adserverTargeting = bids?.adserverTargeting
          const adapterCode = bids?.adapterCode

          let targetingValues: { [key: string]: any } = {}

          for (const [key, value] of Object.entries(adserverTargeting)) {
            targetingValues[`${key}_${adapterCode}`] = value
          }

          resolve(targetingValues)
        } else {
          resolve({})
        }
      } catch (e) {
        reject()
      }
    })
  })
}

export function useHTL(storyKeyValues?: any, scriptLoaded?: boolean) {
  useEffect(() => {
    const uuid = getAnonymousID()
    if (isServer || !uuid || !scriptLoaded) return

    try {
      window.htlbid.cmd = window.htlbid.cmd || []
      window.htlbid.cmd.push(() => {
        for (const [key, value] of Object.entries(storyKeyValues)) {
          if (value) window.htlbid.setTargeting(key, value)
        }

        window.htlbid.setTargeting(
          'is_testing',
          process.env.NODE_ENV === 'production' ? 'no' : 'yes'
        )
        window.htlbid.pubads().setPublisherProvidedId(uuid)
        window.htlbid.layout()
      })
    } catch (e) {
      console.error(e)
    }
  }, [storyKeyValues, scriptLoaded])
}
